import React, { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from 'reactstrap';
import api from '../../services/api';

const attributeFriendlyNameMap = {
  email: 'Email',
  phone_number: 'Phone Number',
};

const attributeMessageMap = {
  email: {
    initial: 'Click below to send a verification link to your email:',
    sending: 'Sending email, please wait.',
    success: 'Success! Please check your email and click on the verification link or enter the verification code below.',
    verifying: 'Verifying code, please wait.',
    errorSending: 'An error occurred. Unable to send verification link.',
    errorVerifying: 'An error occurred. Unable to verify code.',
    done: 'Your email has been verified.',
  },
  phone_number: {
    initial: 'Click below to send a verification code to your phone number:',
    sending: 'Sending SMS, please wait.',
    success: 'Success! Please check your phone for the verification code and enter it below.',
    verifying: 'Verifying code, please wait.',
    errorSending: 'An error occurred. Unable to send verification code.',
    errorVerifying: 'An error occurred. Unable to verify code.',
    done: 'Your phone number has been verified.',
  },
};

export default function SendVerificationModal({
  modalOpen, toggle, onComplete, userAccount, attributeName,
}) {
  const attributeFriendlyName = attributeFriendlyNameMap[attributeName];
  const attributeMessages = attributeMessageMap[attributeName];
  const statusMessages = {
    initial: { status: 'initial', message: attributeMessages.initial },
    sending: { status: 'sending', message: attributeMessages.sending },
    success: { status: 'success', message: attributeMessages.success, className: 'text-primary' },
    verifying: { status: 'verifying', message: attributeMessages.verifying },
    errorSending: { status: 'error', message: attributeMessages.errorSending, className: 'text-danger' },
    errorVerifying: { status: 'error', message: attributeMessages.errorVerifying, className: 'text-danger' },
    done: { status: 'done', message: attributeMessages.done, className: 'text-primary' },
  };

  const [status, setStatus] = useState(statusMessages.initial);
  const [code, setCode] = useState('');

  const sendConfirmation = async () => {
    setStatus(statusMessages.sending);

    const result = await api('SendVerification', {
      accessToken: userAccount.accessToken,
      attributeName,
      origin: window.location.origin,
    });

    if (result?.$metadata?.httpStatusCode === 200) {
      setStatus(statusMessages.success);
    } else {
      setStatus(statusMessages.errorSending);
    }
  };

  const verifyCode = async () => {
    setStatus(statusMessages.verifying);

    const result = await api('CheckVerification', {
      accessToken: userAccount.accessToken,
      attributeName,
      code: code.trim(),
    });

    if (result?.$metadata?.httpStatusCode === 200) {
      setStatus(statusMessages.done);
      onComplete();
    } else {
      setStatus(statusMessages.errorVerifying);
    }
  };

  const sendMessage = status.status === 'success' ? 'Confirm' : 'Send';
  const closeMessage = status.status === 'initial' ? 'Cancel' : 'Close';
  const sendOrVerify = status.status === 'success' ? verifyCode : sendConfirmation;
  const isSendBtnDisabled = ['sending', 'verifying'].includes(status.status);

  return (
    <Modal isOpen={modalOpen}>
      <ModalHeader>
        {attributeFriendlyName}
        {' '}
        Verification
      </ModalHeader>
      <ModalBody>
        {attributeMessages.sendMessage}
        <p className={status.className}>{status.message}</p>
        {(['initial', 'error'].includes(status.status)) && (
        <div className="my-3">
          <Input type="text" className="form-control bg-white" placeholder={userAccount[attributeName]} disabled />
        </div>
        )}
        {status.status === 'success' && (
        <div className="my-3">
          <Input
            type="number"
            className="form-control bg-white"
            placeholder="Enter the verification code"
            onChange={(e) => setCode(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                sendOrVerify();
              }
            }}
          />
        </div>
        )}
      </ModalBody>
      <ModalFooter>
        {status.status !== 'done' && (
          <Button color="primary" onClick={sendOrVerify} disabled={isSendBtnDisabled}>
            {(status.status === 'sending') ? (<Spinner className="d-flex" size="sm" />) : sendMessage}
          </Button>
        )}
        <Button onClick={toggle}>{closeMessage}</Button>
      </ModalFooter>
    </Modal>
  );
}
